import React from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import SourceChip from '../../../capability-setup/SourceChip';
import { sourceOf } from '../../../capability-setup/capabilitySource';
import CloseIcon from '@mui/icons-material/Close';
import ElliottFace from '../../../../components/ElliottFace';
import type { CapabilityRecommendationView } from '../../../../../../api/capability';
import {
  ActionButton,
  BODY,
  BRANDY,
  FONT,
  LABEL,
  LINE,
  SECTION_TITLE,
  TEXT,
  TEXT_2
} from '../../../capability-setup/designSystem';
import { BrandCta } from '../../../capability-setup/learner/ProgramPanel';

/**
 * Why Elliott recommends a Training, shown before the build flow opens: what
 * he has found, what he recommends, and why it helps. The figures and the
 * wording come from the backend with the recommendation.
 */
const RecommendationReasonDialog: React.FC<{
  recommendation: CapabilityRecommendationView | null;
  onClose: () => void;
  /** On to the build flow. */
  onContinue: () => void;
}> = ({ recommendation, onClose, onContinue }) => (
  <Dialog
    open={Boolean(recommendation)}
    onClose={onClose}
    maxWidth={false}
    PaperProps={{
      sx: {
        width: 560,
        maxWidth: '94vw',
        maxHeight: '88vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#1C1C1C',
        backgroundImage: 'none',
        border: '1px solid rgba(247, 148, 32, 0.45)',
        borderRadius: '8px'
      }
    }}
  >
    {recommendation && (
      <>
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            p: '16px 20px',
            borderBottom: `1px solid ${LINE}`
          }}
        >
          <ElliottFace size={22} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...LABEL, color: BRANDY, mb: '6px' }}>Recommended course</Typography>
            <Typography sx={{ ...SECTION_TITLE, whiteSpace: 'normal' }}>{recommendation.course.name}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '6px' }}>
              <Typography sx={{ ...BODY, fontFamily: FONT, fontSize: 12.5 }}>
                {recommendation.roleName} · {recommendation.capabilityName}
              </Typography>
              <SourceChip source={sourceOf(recommendation)} />
            </Box>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} sx={{ color: TEXT_2, '&:hover': { color: TEXT } }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '4px 20px 16px' }}>
          {[
            { label: 'What I found', text: recommendation.reason.identified },
            { label: 'What I recommend', text: recommendation.reason.recommendation },
            { label: 'Why this helps', text: recommendation.reason.benefit }
          ].map((part, i) => (
            <Box
              key={part.label}
              sx={{ py: '14px', ...(i > 0 ? { borderTop: '1px solid rgba(247, 148, 32, 0.45)' } : null) }}
            >
              <Typography sx={{ ...LABEL, mb: '8px' }}>{part.label}</Typography>
              <Typography sx={{ ...BODY, fontFamily: FONT, fontSize: 14, color: TEXT }}>{part.text}</Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            p: '12px 20px',
            borderTop: `1px solid ${LINE}`
          }}
        >
          <ActionButton onClick={onClose}>Not Now</ActionButton>
          <BrandCta onClick={onContinue}>Build Training</BrandCta>
        </Box>
      </>
    )}
  </Dialog>
);

export default RecommendationReasonDialog;
